import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Tables } from "database.types";
import dayjs from "dayjs";
import { formatPrice } from "@/utils/formatPrice";

interface BinderValueChartProps {
  binderId?: string;
  variants: Tables<"variants">[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
  const isVisible = active && payload && payload.length;

  const date = label;
  const value = payload?.[0]?.value;

  const formattedValue = useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  }, [value]);
  return (
    <div
      className="bg-background border border-border rounded-md px-2 py-1 shadow-sm flex items-center gap-2"
      style={{ visibility: isVisible ? "visible" : "hidden" }}
    >
      <span>{formattedValue}</span>
      <span className="text-xs text-muted-foreground">{date}</span>
    </div>
  );
};

const BinderValueChart: React.FC<BinderValueChartProps> = ({ variants }) => {
  const [currentPeriod, setCurrentPeriod] = useState("30d");

  const periods = useMemo(() => {
    return [
      { name: "3d", days: 3 },
      { name: "7d", days: 7 },
      { name: "15d", days: 15 },
      { name: "30d", days: 30 },
    ];
  }, []);

  const currentTotalPrice = useMemo(() => {
    if (variants.length <= 1) {
      return Number(variants[0].price);
    }
    return variants.reduce((acc, v) => {
      return acc + (Number(v.price) || 0);
    }, 0);
  }, [variants]);

  // Build aggregated 30-day binder value history from variants' priceHistory30d
  const aggregatedHistory = useMemo(() => {
    // Build daily buckets for last 30 days including today
    const days: { start: number; end: number; iso: string }[] = [];

    const now = new Date();

    const period = periods.find((p) => p.name === currentPeriod) || periods[0];

    for (let i = period.days - 1; i >= 0; i -= 1) {
      const day = new Date(now);

      day.setHours(0, 0, 0, 0);
      day.setDate(day.getDate() - i);
      const startMs = day.getTime();
      const endMs = startMs + 24 * 60 * 60 * 1000 - 1;

      days.push({
        start: startMs,
        end: endMs,
        iso: new Date(startMs).toISOString(),
      });
    }

    // Preprocess variant histories: sort ascending by time in ms
    const histories = variants.map((v) => {
      const arr = (v.raw?.priceHistory30d ?? [])
        .map((p) => ({ p: p.p, t: p.t * 1000 }))
        .sort((a, b) => a.t - b.t);
      return arr;
    });

    const result = days.map((d) => {
      let total = 0;
      for (const hist of histories) {
        if (hist.length === 0) continue;
        // find last point with t <= d.end
        let lastPrice = 0;
        for (let i = hist.length - 1; i >= 0; i -= 1) {
          const point = hist[i];
          if (point.t <= d.end) {
            lastPrice = point.p;
            break;
          }
        }
        total += lastPrice;
      }
      return { recorded_at: d.iso, total_value: total };
    });

    // If all totals are zero, return empty to show empty state in chart
    const hasAnyValue = result.some((r) => r.total_value > 0);

    return hasAnyValue ? result : [];
  }, [currentPeriod, periods, variants]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const baseHistory = aggregatedHistory;

  const chartData = useMemo(() => {
    const currentDate = new Date(new Date().toISOString());

    const filteredHistory = baseHistory.filter((item) => {
      const itemDate = new Date(item.recorded_at);

      return !dayjs(itemDate).isSame(dayjs(currentDate), "date");
    });

    return [
      ...filteredHistory,
      {
        recorded_at: currentDate.toISOString(),
        total_value: currentTotalPrice,
      },
    ].map((item) => ({
      date: formatDate(item.recorded_at),
      value: parseFloat(item.total_value.toString()),
      fullDate: item.recorded_at,
    }));
  }, [baseHistory, currentTotalPrice]);

  // Calculate change from first to last value
  const firstValue = chartData[0]?.value || 0;
  const lastValue = chartData[chartData.length - 1]?.value || 0;

  const change = lastValue - firstValue;
  const changePercent =
    firstValue > 0 ? Math.abs((change / firstValue) * 100) : "";

  const changeType =
    change === 0 ? "neutral" : change > 0 ? "positive" : "negative";

  const currentPriceFormatted = formatPrice(currentTotalPrice);

  return (
    <Card className="bg-white dark:bg-card border border-gray-200 dark:border-border">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-foreground dark:text-fluorescent-blue flex items-center justify-between">
          <span>Value History</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-normal text-gray-600 dark:text-muted-foreground">
              {currentPriceFormatted}
            </span>
            {change !== 0 && (
              <div
                className={`flex items-center gap-1 text-sm ${changeType === "positive" ? "text-green-600" : changeType === "negative" ? "text-red-600" : "text-gray-600"}`}
              >
                {changeType === "positive" ? (
                  <TrendingUp className="w-4 h-4" />
                ) : changeType === "negative" ? (
                  <TrendingDown className="w-4 h-4" />
                ) : (
                  <Minus className="w-4 h-4" />
                )}
                {changePercent && (
                  <span>
                    {changeType === "positive"
                      ? "+"
                      : changeType === "negative"
                        ? "-"
                        : "+"}
                    {changePercent.toFixed(1)}%
                  </span>
                )}
              </div>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-4 justify-end">
          {periods.map((period) => (
            <Button
              key={period.name}
              variant={currentPeriod === period.name ? "default" : "outline"}
              onClick={() => setCurrentPeriod(period.name)}
              className="text-sm py-0! px-4 h-auto"
              style={{
                paddingTop: "4px",
                paddingBottom: "4px",
              }}
            >
              {period.name}
            </Button>
          ))}
        </div>
        {chartData.length > 1 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient
                  id="positiveGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                </linearGradient>
                <linearGradient
                  id="negativeGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                </linearGradient>
                <linearGradient
                  id="neutralGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor="#bcbbcc" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#bcbbcc" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis
                dataKey="date"
                className="text-xs text-gray-600 dark:text-muted-foreground"
              />
              <YAxis
                className="text-xs text-gray-600 dark:text-muted-foreground"
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip content={CustomTooltip} isAnimationActive={false} />
              <Area
                type="linear"
                dataKey="value"
                stroke={
                  changeType === "negative"
                    ? "#ef4444"
                    : changeType === "positive"
                      ? "#10b981"
                      : "#bcbbcc"
                }
                strokeWidth={2}
                fill={
                  changeType === "negative"
                    ? "url(#negativeGradient)"
                    : changeType === "positive"
                      ? "url(#positiveGradient)"
                      : "url(#neutralGradient)"
                }
                fillOpacity={0.8}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-600 dark:text-muted-foreground">
            <div className="text-center">
              <p className="text-lg font-medium mb-2">No Value History Yet</p>
              <p className="text-sm">
                Add cards to your binder to start tracking value changes
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BinderValueChart;
